import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import {
  ColoringControls,
  IFCEditingSection,
  StoreyImageUploadDrawer,
  WidgetPortal,
} from '@editor/components'
import BuildingForm from '@editor/components/BuildingForm'
import CommentDrawer from '@editor/components/CommentDrawer'
import type { GlbFileAdditionalData } from '@editor/components/Controls/IFCUploadWidget/types/ifcMetadata'
import ElementListPane, {
  COMMENTS_TAB_ID,
  TwoPaneElementDrawer,
} from '@editor/components/ElementListPane'
import OrthographicControls from '@editor/components/OrthographicControls'
import { useCameraStore } from '@modugen/ui-toolkit'
import { CorneredSection } from '@ui2/structure'
import { useControlStore, useEditElementStore, useModelStore } from '@editorStores'
import {
  useEnterEditorPage,
  useIfcFiles,
  useIfcMode,
  useSceneColoring,
  useSetModelColumnCompact,
  useStoreyImages,
} from '@editorHooks'
import GuidedIFCDrawer, { GuidedIFCStep } from './components/GuidedIFCDrawer'
import IFCAlignmentPanel from './components/IFCAlignmentPanel'
import IFCElementEditPanel from './components/IFCElementEditPanel'
import IFCElementImportPanel from './components/IFCElementImportPanel'
import IFCFilterPanel from './components/IFCFilterPanel'
import IFCImportStartInfo from './components/IFCImportStartInfo'
import IFCModeDrawer from './components/IFCModeDrawer'
import IFCOpeningAlignPanel from './components/IFCOpeningAlignPanel'
import IFCOpeningDrawPanel from './components/IFCOpeningDrawPanel'
import IFCOpeningImportPanel from './components/IFCOpeningImportPanel'
import IFCOrientationPanel from './components/IFCOrientationPanel'
import IFCPlanarEditDrawer from './components/IFCPlanarEditDrawer'
import IFCRoofDrawPanel from './components/IFCRoofDrawPanel'
import IFCSettingsPanel from './components/IFCSettingsPanel'
import IFCStoreyAssignmentPanel from './components/IFCStoreyAssignmentPanel'
import IFCTypeAssignmentPanel from './components/IFCTypeAssignmentPanel'
import IFCUploadPanel from './components/IFCUploadPanel'
import IFCWallAutoConnectPanel from './components/IFCWallAutoConnectPanel'
import useIFCGuidedFilterContextMenu from './hooks/useIFCGuidedFilterContextMenu'
import useIFCImportBuildingMode from './hooks/useIFCImportBuildingMode'
import useIFCImportElementList from './hooks/useIFCImportElementList'
import useIFCImportEsc from './hooks/useIFCImportEsc'
import useIFCImportGuided from './hooks/useIFCImportGuided'
import useIFCImportOpeningDrawing from './hooks/useIFCImportOpeningDrawing'
import useIFCImportSelectionModeHotkey from './hooks/useIFCImportSelectionModeHotkey'
import useIFCImportSettings from './hooks/useIFCImportSettings'
import useIFCImportUpload from './hooks/useIFCImportUpload'
import useIFCImportWallDrawing from './hooks/useIFCImportWallDrawing'
import useIFCOpeningSelection from './hooks/useIFCOpeningSelection'
import useIFCPlanarSelection from './hooks/useIFCPlanarSelection'

// Default single-storey height (m) of a freshly created empty model. The "Gebäudedimensionen" step
// counts as done once the storey boundaries differ from this default. Keep in sync with the empty
// model default in state/mutations/project.ts.
const DEFAULT_EMPTY_MODEL_STOREY_HEIGHT = 2.95

// Element types of the import/edit steps. Module-level constants so the panels' memos see a stable
// array. Outer and inner walls share one import and one edit step (each wall keeps the placement of
// its own type).
const ROOF_TYPES: ElementTypes[] = ['roof_slabs']
const WALL_TYPES: ElementTypes[] = ['outer_walls', 'inner_walls']

// Placeholder guided-import steps — pass any number of components; the wizard's stepper is
// generated from this list.
const GUIDED_STEPS: GuidedIFCStep[] = [
  {
    label: 'Hochladen',
    description: 'Wählen Sie die zu importierende IFC-Datei aus.',
    info: 'Laden Sie die IFC-Datei hoch, die als Grundlage für den Import dienen soll.',
    content: <IFCUploadPanel />,
  },
  {
    label: 'Ausrichten',
    description: 'Positionieren Sie das Modell am Koordinatensystem.',
    info: 'Richten Sie das Modell am Koordinatensystem aus, damit Geschosse korrekt erkannt werden.',
    content: <IFCAlignmentPanel />,
  },
  {
    label: 'Orientieren im Modell',
    description: 'Verschaffen Sie sich einen Überblick über das Modell.',
    info: 'Blenden Sie sonstige Elemente aus, um sich besser im Modell zu orientieren. Über die Filter in der Mitte oben können Sie diese jederzeit wieder einblenden.',
    content: <IFCOrientationPanel />,
  },
  {
    label: 'Filtern',
    description: 'Filtern Sie alle Elemente, die Sie nicht importieren wollen.',
    info: 'Blenden Sie Elemente aus, die nicht importiert werden sollen.',
    content: <IFCFilterPanel />,
  },
  {
    label: 'Gebäudedimensionen',
    description: 'Legen Sie die Gebäudedimensionen fest.',
    info: 'Definieren Sie die Geschossgrenzen und die Wandausrichtung des Gebäudes.',
    content: (
      <CorneredSection>
        <BuildingForm hideAdvancedControls />
      </CorneredSection>
    ),
  },
  {
    label: 'Pläne hochladen',
    description: 'Laden Sie je Stockwerk einen Hintergrundplan hoch.',
    info: 'Laden Sie Pläne hoch, die dem importierten Modell zugeordnet werden sollen.',
    content: (
      <CorneredSection>
        <StoreyImageUploadDrawer />
      </CorneredSection>
    ),
  },
  {
    label: 'Typenzuweisungen',
    description: 'Weisen Sie Elemente einem Typ zu.',
    info: 'Markieren Sie IFC-Elemente und weisen Sie ihnen einen Typ zu.',
    content: <IFCTypeAssignmentPanel />,
  },
  {
    label: 'Stockwerkszuweisung',
    description: 'Weisen Sie Elemente den Stockwerken zu.',
    info: 'Markieren Sie IFC-Elemente und weisen Sie sie einem Stockwerk zu.',
    content: <IFCStoreyAssignmentPanel />,
  },
  {
    label: 'Dachflächen importieren',
    description: 'Wählen Sie die zu importierenden Dachflächen.',
    info: 'Alle als Dachflächen zugewiesenen Elemente sind vorausgewählt. Passen Sie die Auswahl bei Bedarf an.',
    content: <IFCElementImportPanel elementTypes={ROOF_TYPES} />,
  },
  {
    label: 'Dachflächen bearbeiten',
    description: 'Bearbeiten Sie die importierten Dachflächen.',
    info: 'Wählen Sie eine Dachfläche im Modell aus, um ihre Geometrie und Eigenschaften zu bearbeiten.',
    content: <IFCElementEditPanel elementTypes={ROOF_TYPES} />,
    selectionMode: 'planar-model-selection',
  },
  {
    label: 'Wände importieren',
    description: 'Wählen Sie die zu importierenden Außen- und Innenwände.',
    info: 'Alle als Außen- oder Innenwände zugewiesenen Elemente sind vorausgewählt. Passen Sie die Auswahl bei Bedarf an. Jedes Element wird mit der Lage seiner Typzuweisung importiert.',
    content: <IFCElementImportPanel elementTypes={WALL_TYPES} />,
  },
  {
    label: 'Öffnungen importieren',
    description: 'Importieren Sie Öffnungen, die nicht automatisch übernommen wurden.',
    info: 'Wählen Sie IFC-Öffnungen im Modell und importieren Sie sie als „Wandöffnung“ in die zugehörige Wand.',
    content: <IFCOpeningImportPanel />,
  },
  {
    label: 'Wände bearbeiten',
    description: 'Bearbeiten Sie die importierten Außen- und Innenwände.',
    info: 'Wählen Sie eine Wand im Modell aus, um ihre Geometrie und Eigenschaften zu bearbeiten. Über das Werkzeug „Zeichnen“ können Sie fehlende Wände als Außen- oder Innenwand im Grundriss einzeichnen. Das Werkzeug „Automatisch“ zeigt einen Vorschlag zur Verbindung nicht verbundener Wände im Grundriss.',
    content: <IFCElementEditPanel elementTypes={WALL_TYPES} />,
    selectionMode: 'planar-model-selection',
  },
  {
    label: 'Öffnungen ausrichten',
    description: 'Richten Sie übereinanderstehende Öffnungen aus.',
    info: 'Untereinanderstehende Öffnungen sollten die gleichen Koordinaten haben. Schnittklammer anlegen, und prüfen ob die Öffnungen, welche untereinander stehen die gleichen Koordinaten haben',
    content: <IFCOpeningAlignPanel />,
    selectionMode: 'planar-model-selection',
  },
  {
    label: 'Import',
    description: 'Überprüfen Sie die Auswahl und schließen Sie den Import ab.',
    info: 'Überprüfen Sie die Auswahl und schließen Sie den Import ab.',
    content: (
      <CorneredSection>
        <Stack spacing={1}>
          <Typography variant="h6">Import abschließen</Typography>
          <Typography variant="body2" color="text.secondary">
            Überprüfen Sie die Auswahl und schließen Sie den Import ab.
          </Typography>
        </Stack>
      </CorneredSection>
    ),
  },
]

const IFCImport = (): ReactElement => {
  useEnterEditorPage('ifc-import')

  const storey = useControlStore(state => state.floorplanState.storey)
  const setFloorplanStorey = useControlStore(state => state.setFloorplanStorey)
  const setIsOrthographic = useCameraStore(state => state.setIsOrthographic)

  const selectionMode = useControlStore(state => state.selectionMode)
  const setElementSelectionDisabled = useControlStore(state => state.setElementSelectionDisabled)
  const setShowGltfModel = useControlStore(state => state.setShowGltfModel)
  const setSelectionMode = useControlStore(state => state.setSelectionMode)
  const setModelColumnCompact = useSetModelColumnCompact()

  // Show the IFC/GLB model when the page opens; afterwards its visibility is controlled by the
  // "anzeigen" toggle only (so it doesn't flicker on/off between steps). Reset on leave.
  useEffect(() => {
    setShowGltfModel(true)
    return () => setShowGltfModel(false)
  }, [setShowGltfModel])

  // Collapse the storey-controls "Modell" column on this page (it competes for horizontal space
  // with the IFC column here); expand it again when leaving the view.
  useEffect(() => {
    setModelColumnCompact(true)
    return () => setModelColumnCompact(false)
  }, [setModelColumnCompact])

  // `selectionMode` is global and defaults to 'ifc-selection'; the toolbar's planar button can flip
  // it. Reset it on leave (unmount only) so re-entering the page — or another editor page — doesn't
  // inherit a stale 'planar-model-selection' that leaves the IFC model unselectable.
  useEffect(() => () => setSelectionMode('ifc-selection'), [setSelectionMode])

  // Map the selection mode onto the scene: IFC selection makes the GLB model selectable and
  // disables planar-element selection; planar-model selection does the opposite. Reset on leave so
  // it does not leak into other editor pages.
  useEffect(() => {
    const ifcSelection = selectionMode === 'ifc-selection'
    setElementSelectionDisabled(ifcSelection)

    return () => {
      setElementSelectionDisabled(false)
    }
  }, [selectionMode, setElementSelectionDisabled])

  const { isBuildingActive } = useIFCImportBuildingMode()

  const { isSettingsActive } = useIFCImportSettings()

  const { ifcMode } = useIfcMode()

  const isAlignmentActive = ifcMode === 'alignment'

  const isFilterActive = ifcMode === 'filter'

  const isStoreyActive = ifcMode === 'storey'

  const isTypeActive = ifcMode === 'type'

  const isRoofDrawActive = ifcMode === 'draw-roofs'

  const isWallDrawActive = ifcMode === 'draw-walls'

  const isOpeningDrawActive = ifcMode === 'draw-openings'

  const isWallAutoConnectActive = ifcMode === 'auto-connect'

  const { isAddActive } = useIFCImportUpload()

  const { isGuidedActive, toggleGuided, closeGuided } = useIFCImportGuided()

  // Right-click → "Filtern" on IFC elements is available in every guided step (outside the guided
  // flow the element form registers its own action).
  useIFCGuidedFilterContextMenu(isGuidedActive)

  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const uploadStepCompleted = useControlStore(state => state.ifcGuidedUploadCompleted)

  const glbFiles = useIfcFiles()
  const hasIfcFiles = glbFiles.length > 0

  const activeFileData = glbFiles.find((f: FileItem) => f.guid === activeIfcFileGuid)
    ?.additional_data as GlbFileAdditionalData | undefined
  const alignmentConfig = activeFileData?.alignment_config
  const metadata = activeFileData?.model_metadata

  // The model counts as aligned once the active file has a non-identity alignment config.
  const isAligned =
    !!alignmentConfig &&
    (alignmentConfig.zRotationDegrees !== 0 ||
      alignmentConfig.referencePointIFCCS.some(coord => coord !== 0))
  const hasFilter = (metadata?.filteredElements ?? []).some(group => group.length > 0)
  const hasTypeAssignment = (metadata?.elementTypeAssignments ?? []).some(group => group.length > 0)
  const hasStoreyAssignment = (metadata?.storeyAssignments ?? []).some(group => group.length > 0)

  // "Pläne hochladen" uploads a background image per storey; it counts as done once any exists.
  const { storeyImages } = useStoreyImages()
  const hasStoreyImages = Object.keys(storeyImages).length > 0

  // The building dimensions count as configured once the storey boundaries differ from the default
  // single 2.95 m storey of a freshly created model.
  const storeyBoundaries = useModelStore(state => state.model.storey_boundaries)
  const isBuildingConfigured = useMemo(() => {
    const entries = Object.entries(storeyBoundaries)
    if (entries.length === 0) return false
    const isDefault =
      entries.length === 1 &&
      entries[0][0] === '0' &&
      entries[0][1][0] === 0 &&
      Math.abs(entries[0][1][1] - DEFAULT_EMPTY_MODEL_STOREY_HEIGHT) < 1e-6
    return !isDefault
  }, [storeyBoundaries])

  // Per-step guided state (keyed by label so it survives step reordering). The upload step is also
  // skipped once done; every other step is marked completed but stays reachable.
  const guidedSteps = useMemo(() => {
    const completedByLabel: Record<string, boolean> = {
      Ausrichten: isAligned,
      'Pläne hochladen': hasStoreyImages,
      Filtern: hasFilter,
      Gebäudedimensionen: isBuildingConfigured,
      Typenzuweisungen: hasTypeAssignment,
      Stockwerkszuweisung: hasStoreyAssignment,
    }
    return GUIDED_STEPS.map(step => {
      if (step.label === 'Hochladen') {
        return { ...step, completed: uploadStepCompleted, skipped: uploadStepCompleted }
      }
      const completed = step.label ? completedByLabel[step.label] : undefined
      return completed === undefined ? step : { ...step, completed }
    })
  }, [
    uploadStepCompleted,
    isAligned,
    hasStoreyImages,
    hasFilter,
    isBuildingConfigured,
    hasTypeAssignment,
    hasStoreyAssignment,
  ])

  // Apply the coloring-widget mapping (problems, storeys, …) to the planar model — the widget only
  // computes the mapping; without this hook the scene never receives it.
  useSceneColoring()

  useIFCImportEsc()

  // Hotkey "a" toggles between planar-model and IFC selection (mirrors the toolbar buttons).
  useIFCImportSelectionModeHotkey()

  // Store-driven selection + editing of planar-model elements (walls, slabs, roof slabs) while the
  // planar-model selection mode is active.
  useIFCPlanarSelection()

  // "Wände (2D)" drawing tool: injects the structural-planning wall drawer into the scene while the
  // draw-walls mode is active in the 2D view (the guided wall edit steps run their own instance
  // via their "Zeichnen" tool).
  useIFCImportWallDrawing(isWallDrawActive)
  const activeElement = useEditElementStore(state => state.activeElement)
  const activeOpening = useEditElementStore(state => state.activeOpening)
  const multiActiveElements = useEditElementStore(state => state.multiActiveElements)
  const multiActiveOpenings = useEditElementStore(state => state.multiActiveOpenings)
  const setMultiActiveElements = useEditElementStore(state => state.setMultiActiveElements)
  const isPlanarSelectionActive = selectionMode === 'planar-model-selection'

  // "in Draufsicht bearbeiten" tool: injects the structural-planning single-wall elevation view and
  // its opening drawer into the scene while the draw-openings mode is active.
  useIFCImportOpeningDrawing(isOpeningDrawActive, activeElement, activeOpening)

  // Store-driven selection of wall openings while editing the planar model outside the guided flow
  // (the guided "Öffnungen ausrichten" step enables it on its own panel). Multi-select is enabled so
  // several openings can be edited/aligned together, matching the guided step. Disabled while the
  // auto-connect or opening-draw tool is active so canvas clicks don't compete with their own click
  // handling.
  useIFCOpeningSelection(
    isPlanarSelectionActive && !isGuidedActive && !isWallAutoConnectActive && !isOpeningDrawActive,
  )

  const elementListBinding = useIFCImportElementList()
  const [activePrimary, setActivePrimary] = useState<string>('')

  const elementListCollapsed = useControlStore(state => state.elementListCollapsed)

  const isCommentsTab = !elementListCollapsed && activePrimary === COMMENTS_TAB_ID

  // Default drawer content (no IFC mode active): a start hint when the project has no IFC file yet,
  // otherwise the "Bearbeitung" editing tools.
  const drawerContent = hasIfcFiles ? (
    <IFCEditingSection />
  ) : (
    <Box sx={{ p: 1.5 }}>
      <IFCImportStartInfo onStart={toggleGuided} />
    </Box>
  )

  let detailContent = drawerContent
  if (isGuidedActive) detailContent = <GuidedIFCDrawer steps={guidedSteps} onExit={closeGuided} />
  else if (isAddActive)
    detailContent = (
      <IFCModeDrawer mode="add">
        <IFCUploadPanel />
      </IFCModeDrawer>
    )
  else if (isSettingsActive)
    detailContent = (
      <IFCModeDrawer mode="settings">
        <IFCSettingsPanel />
      </IFCModeDrawer>
    )
  else if (isAlignmentActive)
    detailContent = (
      <IFCModeDrawer mode="alignment">
        <IFCAlignmentPanel />
      </IFCModeDrawer>
    )
  else if (isFilterActive)
    detailContent = (
      <IFCModeDrawer mode="filter">
        <IFCFilterPanel />
      </IFCModeDrawer>
    )
  else if (isStoreyActive)
    detailContent = (
      <IFCModeDrawer mode="storey">
        <IFCStoreyAssignmentPanel />
      </IFCModeDrawer>
    )
  else if (isTypeActive)
    detailContent = (
      <IFCModeDrawer mode="type">
        <IFCTypeAssignmentPanel />
      </IFCModeDrawer>
    )
  else if (isBuildingActive)
    detailContent = (
      <IFCModeDrawer mode="building">
        <CorneredSection>
          <BuildingForm />
        </CorneredSection>
      </IFCModeDrawer>
    )
  else if (isRoofDrawActive)
    detailContent = (
      <IFCModeDrawer mode="draw-roofs">
        <IFCRoofDrawPanel />
      </IFCModeDrawer>
    )
  else if (isWallDrawActive) detailContent = <IFCModeDrawer mode="draw-walls" />
  else if (isOpeningDrawActive)
    detailContent = (
      <IFCModeDrawer mode="draw-openings">
        <IFCOpeningDrawPanel />
      </IFCModeDrawer>
    )
  else if (isWallAutoConnectActive)
    detailContent = (
      <IFCModeDrawer mode="auto-connect">
        <IFCWallAutoConnectPanel />
      </IFCModeDrawer>
    )
  else if (
    isPlanarSelectionActive &&
    (activeElement || multiActiveElements.length > 0 || multiActiveOpenings.length > 0)
  )
    detailContent = <IFCPlanarEditDrawer />
  else if (isCommentsTab) detailContent = <CommentDrawer />

  return (
    <>
      <WidgetPortal>
        <Stack direction="row" spacing={1}>
          <OrthographicControls
            isOrthographic={!!storey}
            setIsOrthographic={ortho => {
              if (ortho) {
                setFloorplanStorey('0')
                setIsOrthographic(true)
              } else {
                setFloorplanStorey(null)
                setIsOrthographic(false)
              }
            }}
          />
          <ColoringControls onSelectElements={setMultiActiveElements} />
        </Stack>
      </WidgetPortal>

      <TwoPaneElementDrawer
        collapsed={elementListCollapsed}
        list={<ElementListPane {...elementListBinding} onActivePrimaryChange={setActivePrimary} />}
      >
        {detailContent}
      </TwoPaneElementDrawer>
    </>
  )
}

export default IFCImport
